import { useState, useCallback, useRef } from "react";

// ─── CDN LOADER ───────────────────────────────────────────────────────────────
function loadScript(src) {
  return new Promise((res, rej) => {
    if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
    const s = document.createElement("script");
    s.src = src; s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function formatBytes(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(2)} MB`;
}

function savings(original, compressed) {
  if (!original || !compressed) return null;
  const pct = ((1 - compressed / original) * 100).toFixed(1);
  return { pct, bigger: compressed > original };
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 3000);
}

// ─── COPY BUTTON ─────────────────────────────────────────────────────────────
function CopyBtn({ text, t }) {
  const [copied, setCopied] = useState(false);
  return (
    <button className="btn-sm"
      onClick={() => navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1600); })}
      style={{ borderColor: copied ? t.statusOk : t.border, color: copied ? t.statusOk : t.textMuted }}>
      {copied ? "✓ Copied" : "⎘ Copy"}
    </button>
  );
}

// ─── STATS BAR ───────────────────────────────────────────────────────────────
function StatsBar({ original, compressed, t }) {
  const s = savings(original, compressed);
  if (!s) return null;
  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", padding: "10px 14px", borderTop: `1px solid ${t.border}`, alignItems: "center" }}>
      {[
        { label: "Original",   val: formatBytes(original) },
        { label: "Compressed", val: formatBytes(compressed) },
        { label: "Saved",      val: `${s.bigger ? "-" : ""}${s.pct}%`, color: s.bigger ? t.statusErr : t.statusOk },
      ].map(({ label, val, color }) => (
        <div key={label} style={{ textAlign: "center" }}>
          <div style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 2 }}>{label}</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: color || t.textAccent, fontFamily: "JetBrains Mono" }}>{val}</div>
        </div>
      ))}
      {!s.bigger && (
        <div style={{ marginLeft: "auto" }}>
          <div style={{ height: 6, width: 140, background: t.border, borderRadius: 3, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${100 - parseFloat(s.pct)}%`, background: t.accentMain, borderRadius: 3, transition: "width 0.4s" }} />
          </div>
          <div style={{ fontSize: 10, color: t.textMuted, marginTop: 3, textAlign: "right" }}>{100 - parseFloat(s.pct)}% of original</div>
        </div>
      )}
    </div>
  );
}

// ─── DROP ZONE ────────────────────────────────────────────────────────────────
function DropZone({ accept, multiple, onFiles, dragOver, setDragOver, files, label, t }) {
  const ref = useRef(null);
  return (
    <div
      style={{
        border: `2px dashed ${dragOver ? t.borderActive : files.length ? t.borderActive : t.dragBorder}`,
        borderRadius: 14, background: dragOver ? t.bgChipActive : t.dragBg,
        padding: "1.75rem 1.5rem", textAlign: "center", cursor: "pointer",
        transition: "all 0.2s", color: dragOver ? t.textAccent : files.length ? t.textAccent : t.dragText,
        marginBottom: "1rem",
      }}
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => { e.preventDefault(); setDragOver(false); onFiles(Array.from(e.dataTransfer.files)); }}
      onClick={() => ref.current?.click()}
    >
      <div style={{ fontSize: 28, marginBottom: 8 }}>{files.length ? "✓" : "⬆"}</div>
      {files.length ? (
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{files.length} file{files.length > 1 ? "s" : ""} ready</div>
          <div style={{ fontSize: 12, color: t.textMuted }}>{files.map(f => f.name).join(", ").slice(0, 80)}{files.map(f => f.name).join(", ").length > 80 ? "…" : ""}</div>
        </div>
      ) : (
        <div>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Drop your <strong style={{ color: t.textAccent }}>{label}</strong> here</div>
          <div style={{ fontSize: 12 }}>or <strong style={{ color: t.textAccent }}>click to browse</strong></div>
        </div>
      )}
      <input ref={ref} type="file" accept={accept} multiple={multiple} style={{ display: "none" }}
        onChange={e => onFiles(Array.from(e.target.files))} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// 1. IMAGE COMPRESSOR
// ══════════════════════════════════════════════════════════════════════════════
function ImageCompressor({ t }) {
  const [files, setFiles]       = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [quality, setQuality]   = useState(80);
  const [maxWidth, setMaxWidth] = useState(0);       // 0 = no resize
  const [maxSizeKB, setMaxSizeKB] = useState(0);     // 0 = no size target
  const [format, setFormat]     = useState("auto");  // auto | jpeg | png | webp
  const [results, setResults]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [progress, setProgress] = useState("");

  const compress = useCallback(async () => {
    if (!files.length) return;
    setLoading(true); setResults([]);

    await loadScript("https://cdn.jsdelivr.net/npm/browser-image-compression@2.0.2/dist/browser-image-compression.js");
    const imageCompression = window.imageCompression;

    const out = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setProgress(`Compressing ${i + 1} of ${files.length}: ${file.name}...`);
      try {
        // ── STRATEGY ─────────────────────────────────────────────────────────
        // If maxSizeKB is set:
        //   - maxSizeMB is the HARD ceiling — output will NEVER exceed it
        //   - initialQuality is the starting point — library reduces it as needed
        //   - The library iterates quality downward automatically until size fits
        // If maxSizeKB is NOT set (0):
        //   - Only quality matters — compress at exactly the chosen quality
        // ─────────────────────────────────────────────────────────────────────
        const hasSizeTarget = maxSizeKB > 0;

        const opts = {
          // Hard ceiling: if size target set use it, otherwise no limit
          maxSizeMB:            hasSizeTarget ? maxSizeKB / 1024 : 999,
          maxWidthOrHeight:     maxWidth > 0 ? maxWidth : undefined,
          // Quality is always the starting point — library goes lower if needed to meet size
          initialQuality:       quality / 100,
          useWebWorker:         true,
          fileType:             format === "auto" ? undefined : `image/${format}`,
          alwaysKeepResolution: maxWidth === 0,
          // Tell the library to strictly enforce the size limit
          // by allowing it to reduce quality as far as needed
          maxIteration:         hasSizeTarget ? 20 : 10,
        };

        const compressed = await imageCompression(file, opts);

        const ext  = format === "auto" ? file.name.split(".").pop() : format;
        const name = file.name.replace(/[.][^.]+$/, "") + `_compressed.${ext}`;

        // Check if we still exceeded target (only possible if image is already
        // smaller than target or format cannot compress further e.g. tiny PNG)
        const exceedsTarget = hasSizeTarget && compressed.size > maxSizeKB * 1024;

        out.push({
          name,
          blob:           compressed,
          originalSize:   file.size,
          compressedSize: compressed.size,
          exceedsTarget,
          type:           "image",
        });
      } catch (e) {
        out.push({ name: file.name, error: e.message, originalSize: file.size });
      }
    }
    setResults(out);
    setProgress("");
    setLoading(false);
  }, [files, quality, maxWidth, maxSizeKB, format]);

  const downloadAll = () => results.filter(r => !r.error).forEach(r => downloadBlob(r.blob, r.name));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <DropZone accept="image/jpeg,image/png,image/webp,image/gif,image/bmp"
        multiple label="JPG / PNG / WebP images"
        files={files} onFiles={setFiles} dragOver={dragOver} setDragOver={setDragOver} t={t} />

      {/* Settings */}
      <div className="panel">
        <div style={{ padding: "8px 14px", borderBottom: `1px solid ${t.border}`, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: t.textAccent, background: t.bgPanelHead }}>
          Compression Settings
        </div>
        <div style={{ padding: "14px", display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Quality slider */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: t.textLabel, fontFamily: "JetBrains Mono" }}>Quality</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: t.textAccent, fontFamily: "JetBrains Mono" }}>{quality}%</span>
            </div>
            <input type="range" min={1} max={100} value={quality} step={1}
              onChange={e => setQuality(+e.target.value)} style={{ width: "100%" }} />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: t.textMuted, marginTop: 3 }}>
              <span>Smallest file</span><span>Best quality</span>
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {/* Max file size target */}
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 6, fontFamily: "JetBrains Mono" }}>Max File Size (KB) <span style={{ opacity: 0.5 }}>— 0 = no limit, overrides quality</span></div>
              <input type="number" min={0} value={maxSizeKB} onChange={e => setMaxSizeKB(Math.max(0, +e.target.value))}
                style={{ width: "100%", background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 7, padding: "7px 10px", fontSize: 13, fontFamily: "JetBrains Mono", color: t.textPrimary, outline: "none" }} />
            </div>
            {/* Max width */}
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 6, fontFamily: "JetBrains Mono" }}>Max Width (px) <span style={{ opacity: 0.5 }}>— 0 = no resize</span></div>
              <input type="number" min={0} value={maxWidth} onChange={e => setMaxWidth(Math.max(0, +e.target.value))}
                style={{ width: "100%", background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 7, padding: "7px 10px", fontSize: 13, fontFamily: "JetBrains Mono", color: t.textPrimary, outline: "none" }} />
            </div>
            {/* Output format */}
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 6, fontFamily: "JetBrains Mono" }}>Output Format</div>
              <select value={format} onChange={e => setFormat(e.target.value)}
                style={{ width: "100%", background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 7, padding: "7px 10px", fontSize: 13, fontFamily: "JetBrains Mono", color: t.textAccent, outline: "none" }}>
                <option value="auto">Auto (keep original)</option>
                <option value="jpeg">JPEG</option>
                <option value="png">PNG</option>
                <option value="webp">WebP (smallest)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* How quality + size target work together */}
      <div style={{ background: t.bgChipActive, border: `1px solid ${t.border}`, borderRadius: 10, padding: "9px 14px", fontSize: 12, color: t.textSecondary, display: "flex", gap: 8 }}>
        <span style={{ flexShrink: 0 }}>ℹ</span>
        <span>
          <strong style={{ color: t.textPrimary }}>Max File Size</strong> is a strict hard limit — output will never exceed it.
          {" "}<strong style={{ color: t.textPrimary }}>Quality</strong> is the starting point — if the chosen quality produces a file larger than your limit, quality is automatically reduced until the file fits within the target.
        </span>
      </div>

      <button className="btn-primary" onClick={compress} disabled={!files.length || loading}
        style={{ padding: "11px 28px", fontSize: 14, alignSelf: "flex-start" }}>
        {loading ? `⏳ ${progress}` : `⚡ Compress ${files.length > 1 ? `${files.length} Images` : "Image"}`}
      </button>

      {/* Results */}
      {results.length > 0 && (
        <div className="panel active-panel">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 14px", background: t.bgPanelHead, borderBottom: `1px solid ${t.border}` }}>
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: t.textAccent }}>
              Results — {results.length} file{results.length > 1 ? "s" : ""}
            </span>
            {results.length > 1 && <button className="btn-sm" onClick={downloadAll}>↓ Download All</button>}
          </div>
          {results.map((r, i) => (
            <div key={i} style={{ padding: "10px 14px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 20 }}>🖼</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: t.textPrimary, marginBottom: 2 }}>{r.name}</div>
                {r.error ? (
                  <div style={{ fontSize: 11, color: t.statusErr }}>{r.error}</div>
                ) : (
                  <>
                    <div style={{ fontSize: 11, color: t.textMuted, fontFamily: "JetBrains Mono" }}>
                      {formatBytes(r.originalSize)} → {formatBytes(r.compressedSize)}
                      {" "}<span style={{ color: r.compressedSize < r.originalSize ? t.statusOk : t.statusErr, fontWeight: 600 }}>
                        ({savings(r.originalSize, r.compressedSize)?.bigger ? "larger" : `-${savings(r.originalSize, r.compressedSize)?.pct}%`})
                      </span>
                    </div>
                    {r.exceedsTarget && (
                      <div style={{ fontSize: 11, color: t.dotAmber, marginTop: 2, fontFamily: "JetBrains Mono" }}>
                        ⚠ Could not reach {maxSizeKB} KB — try lowering quality or adding a max width
                      </div>
                    )}
                  </>
                )}
              </div>
              {!r.error && <button className="btn-primary" onClick={() => downloadBlob(r.blob, r.name)} style={{ padding: "5px 14px", fontSize: 12 }}>↓</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// 2. PDF COMPRESSOR
// ══════════════════════════════════════════════════════════════════════════════
function PdfCompressor({ t }) {
  const [file, setFile]         = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [quality, setQuality]   = useState(75);
  const [result, setResult]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [progress, setProgress] = useState("");

  const compress = useCallback(async () => {
    if (!file) return;
    setLoading(true); setResult(null);

    try {
      setProgress("Loading PDF…");
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js");
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");

      const pdfjsLib = window["pdfjs-dist/build/pdf"];
      pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const total = pdf.numPages;
      const { jsPDF } = window.jspdf;
      let outPdf = null;

      for (let i = 1; i <= total; i++) {
        setProgress(`Re-rendering page ${i} of ${total}…`);
        const page = await pdf.getPage(i);
        const scale = quality < 50 ? 1.0 : quality < 75 ? 1.5 : 2.0;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width; canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;

        const imgData = canvas.toDataURL("image/jpeg", quality / 100);
        const ptW = viewport.width * 0.75;
        const ptH = viewport.height * 0.75;
        const orientation = ptW > ptH ? "landscape" : "portrait";

        if (!outPdf) {
          outPdf = new jsPDF({ orientation, unit: "pt", format: [ptW, ptH], compress: true });
        } else {
          outPdf.addPage([ptW, ptH], orientation);
        }
        outPdf.addImage(imgData, "JPEG", 0, 0, ptW, ptH, undefined, "FAST");
      }

      setProgress("Finalizing…");
      const bytes = outPdf.output("arraybuffer");
      const blob  = new Blob([bytes], { type: "application/pdf" });
      setResult({ blob, compressedSize: blob.size, originalSize: file.size, name: file.name.replace(".pdf", "_compressed.pdf") });
    } catch (e) {
      setResult({ error: e.message });
    }
    setProgress(""); setLoading(false);
  }, [file, quality]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <DropZone accept="application/pdf" multiple={false} label="PDF file"
        files={file ? [file] : []} onFiles={f => { setFile(f[0]); setResult(null); }}
        dragOver={dragOver} setDragOver={setDragOver} t={t} />

      <div style={{ display: "flex", alignItems: "center", gap: 12, background: t.bgSettingGrp, border: `1px solid ${t.border}`, borderRadius: 10, padding: "10px 14px" }}>
        <span style={{ fontSize: 12, color: t.textLabel, fontFamily: "JetBrains Mono", whiteSpace: "nowrap" }}>Image Quality</span>
        <input type="range" min={20} max={95} value={quality} step={5}
          onChange={e => setQuality(+e.target.value)} style={{ flex: 1 }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: t.textAccent, fontFamily: "JetBrains Mono", minWidth: 40 }}>{quality}%</span>
      </div>

      <div style={{ background: t.bgChipActive, border: `1px solid ${t.border}`, borderRadius: 10, padding: "9px 14px", fontSize: 12, color: t.textSecondary, display: "flex", alignItems: "center", gap: 8 }}>
        <span>ℹ</span> PDF compression works by re-rendering pages as images. Lower quality = smaller file. Text-based PDFs may lose sharpness at low quality settings.
      </div>

      <button className="btn-primary" onClick={compress} disabled={!file || loading}
        style={{ padding: "11px 28px", fontSize: 14, alignSelf: "flex-start" }}>
        {loading ? `⏳ ${progress}` : "⚡ Compress PDF"}
      </button>

      {result && (
        <div className="panel active-panel">
          {result.error ? (
            <div style={{ padding: "14px", color: t.statusErr, fontSize: 13 }}>✕ {result.error}</div>
          ) : (
            <>
              <div style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 24 }}>📄</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: t.textPrimary, marginBottom: 2 }}>{result.name}</div>
                </div>
                <button className="btn-primary" onClick={() => downloadBlob(result.blob, result.name)} style={{ padding: "6px 18px", fontSize: 12 }}>↓ Download</button>
              </div>
              <StatsBar original={result.originalSize} compressed={result.compressedSize} t={t} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// 3. ZIP / ARCHIVE COMPRESSOR  (uses fflate — lossless)
// ══════════════════════════════════════════════════════════════════════════════
function ZipCompressor({ t }) {
  const [files, setFiles]       = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [level, setLevel]       = useState(6);     // 0-9 compression level
  const [result, setResult]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [progress, setProgress] = useState("");
  const [archiveName, setArchiveName] = useState("archive");

  const compress = useCallback(async () => {
    if (!files.length) return;
    setLoading(true); setResult(null);

    try {
      setProgress("Loading compression engine…");
      await loadScript("https://cdn.jsdelivr.net/npm/fflate@0.8.2/umd/index.js");
      const fflate = window.fflate;
      const zipObj = {};

      for (let i = 0; i < files.length; i++) {
        setProgress(`Reading ${i + 1} of ${files.length}: ${files[i].name}…`);
        const buf = await files[i].arrayBuffer();
        zipObj[files[i].name] = [new Uint8Array(buf), { level }];
      }

      setProgress("Compressing…");
      const zipped = await new Promise((res, rej) => {
        fflate.zip(zipObj, { level }, (err, data) => err ? rej(err) : res(data));
      });

      const blob = new Blob([zipped], { type: "application/zip" });
      const totalOriginal = files.reduce((s, f) => s + f.size, 0);
      setResult({ blob, compressedSize: blob.size, originalSize: totalOriginal, name: `${archiveName || "archive"}.zip` });
    } catch (e) {
      setResult({ error: e.message });
    }
    setProgress(""); setLoading(false);
  }, [files, level, archiveName]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <DropZone accept="*" multiple label="any files (all types accepted)"
        files={files} onFiles={f => { setFiles(f); setResult(null); }}
        dragOver={dragOver} setDragOver={setDragOver} t={t} />

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: 2, minWidth: 180, display: "flex", alignItems: "center", gap: 10, background: t.bgSettingGrp, border: `1px solid ${t.border}`, borderRadius: 10, padding: "8px 14px" }}>
          <span style={{ fontSize: 12, color: t.textLabel, fontFamily: "JetBrains Mono", whiteSpace: "nowrap" }}>Compression Level</span>
          <input type="range" min={0} max={9} value={level} step={1}
            onChange={e => setLevel(+e.target.value)} style={{ flex: 1 }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: t.textAccent, fontFamily: "JetBrains Mono", minWidth: 16 }}>{level}</span>
        </div>
        <div style={{ flex: 1, minWidth: 160, display: "flex", alignItems: "center", gap: 8, background: t.bgSettingGrp, border: `1px solid ${t.border}`, borderRadius: 10, padding: "8px 14px" }}>
          <span style={{ fontSize: 12, color: t.textLabel, fontFamily: "JetBrains Mono", whiteSpace: "nowrap" }}>Archive name</span>
          <input value={archiveName} onChange={e => setArchiveName(e.target.value)}
            style={{ flex: 1, background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 6, padding: "4px 8px", fontSize: 12, fontFamily: "JetBrains Mono", color: t.textPrimary, outline: "none" }} />
          <span style={{ fontSize: 12, color: t.textMuted, fontFamily: "JetBrains Mono" }}>.zip</span>
        </div>
      </div>

      <div style={{ background: t.bgChipActive, border: `1px solid ${t.border}`, borderRadius: 10, padding: "9px 14px", fontSize: 12, color: t.textSecondary, display: "flex", gap: 8 }}>
        <span>🔒</span> ZIP compression is <strong style={{ color: t.statusOk }}>lossless</strong> — all files are perfectly restored when extracted. Level 0 = no compression (fastest), Level 9 = maximum compression (slowest).
      </div>

      <button className="btn-primary" onClick={compress} disabled={!files.length || loading}
        style={{ padding: "11px 28px", fontSize: 14, alignSelf: "flex-start" }}>
        {loading ? `⏳ ${progress}` : `⚡ Create ZIP (${files.length} file${files.length > 1 ? "s" : ""})`}
      </button>

      {result && (
        <div className="panel active-panel">
          {result.error ? (
            <div style={{ padding: 14, color: t.statusErr, fontSize: 13 }}>✕ {result.error}</div>
          ) : (
            <>
              <div style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 24 }}>🗜</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: t.textPrimary }}>{result.name}</div>
                  <div style={{ fontSize: 11, color: t.textMuted, fontFamily: "JetBrains Mono" }}>{files.length} files zipped · Level {level}</div>
                </div>
                <button className="btn-primary" onClick={() => downloadBlob(result.blob, result.name)} style={{ padding: "6px 18px", fontSize: 12 }}>↓ Download</button>
              </div>
              <StatsBar original={result.originalSize} compressed={result.compressedSize} t={t} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// 4. SVG OPTIMIZER  (lossless — removes metadata, comments, redundant attrs)
// ══════════════════════════════════════════════════════════════════════════════
function SvgOptimizer({ t }) {
  const [input, setInput]   = useState("");
  const [output, setOutput] = useState("");
  const [files, setFiles]   = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [mode, setMode]     = useState("paste"); // paste | upload

  const SAMPLE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
  <!-- This is a comment -->
  <metadata>Created by Illustrator</metadata>
  <rect x="10.0000" y="10.0000" width="80.0000" height="80.0000"
    fill="blue" stroke="none" stroke-width="1" opacity="1.0"
    style="fill:blue;" />
  <circle cx="50.00" cy="50.00" r="30.00" fill="red" opacity="1" />
</svg>`;

  const optimize = useCallback((svgStr) => {
    if (!svgStr.trim()) return "";
    let s = svgStr;
    // Remove XML declaration
    s = s.replace(/<\?xml[^>]*\?>/gi, "");
    // Remove comments
    s = s.replace(/<!--[\s\S]*?-->/g, "");
    // Remove metadata / title / desc
    s = s.replace(/<metadata[\s\S]*?<\/metadata>/gi, "");
    s = s.replace(/<title[\s\S]*?<\/title>/gi, "");
    s = s.replace(/<desc[\s\S]*?<\/desc>/gi, "");
    // Remove editor namespaces (inkscape, sodipodi, adobe, sketch)
    s = s.replace(/\s(inkscape|sodipodi|xlink|dc|cc|rdf|sketch|adobe|illustrator):[a-z\-:]+="[^"]*"/gi, "");
    s = s.replace(/<(inkscape|sodipodi|metadata|rdf|cc|dc)[^>]*>[\s\S]*?<\/\1>/gi, "");
    // Remove style="" when empty
    s = s.replace(/\s?style=""/g, "");
    // Remove redundant defaults
    s = s.replace(/\sstroke="none"/g, "");
    s = s.replace(/\sstroke-width="1(?:\.0+)?"/g, "");
    s = s.replace(/\sopacity="1(?:\.0+)?"/g, "");
    s = s.replace(/\sfill-opacity="1(?:\.0+)?"/g, "");
    // Remove duplicate style that matches explicit attr (e.g. fill="blue" style="fill:blue;")
    s = s.replace(/\sstyle="fill:([^;]+);?"\s+fill="\1"/g, ' fill="$1"');
    s = s.replace(/\sfill="([^"]+)"\s+style="fill:\1;?"/g, ' fill="$1"');
    // Trim decimal precision (4 zeros → remove, keep max 2 decimal places)
    s = s.replace(/(\d+)\.0{2,}/g, "$1");
    s = s.replace(/(\d+\.\d{2})\d+/g, "$1");
    // Remove extra whitespace between tags
    s = s.replace(/>\s{2,}</g, "><");
    // Remove leading/trailing whitespace from attributes
    s = s.replace(/\s{2,}/g, " ");
    // Trim
    s = s.trim();
    return s;
  }, []);

  const handleInput = (val) => {
    setInput(val);
    setOutput(optimize(val));
  };

  const handleFiles = (fs) => {
    setFiles(fs);
    if (fs[0]) {
      const reader = new FileReader();
      reader.onload = e => { handleInput(e.target.result); };
      reader.readAsText(fs[0]);
    }
  };

  const originalSize  = new Blob([input]).size;
  const compressedSize = new Blob([output]).size;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div style={{ display: "flex", gap: 6, marginBottom: 4 }}>
        {[{id:"paste",label:"Paste SVG"},{id:"upload",label:"Upload File"}].map(m => (
          <button key={m.id} className="btn-sm" onClick={() => setMode(m.id)}
            style={{ borderColor: mode===m.id ? t.borderActive : t.border, color: mode===m.id ? t.textAccent : t.textMuted, background: mode===m.id ? t.bgChipActive : "none", padding: "5px 14px" }}>
            {m.label}
          </button>
        ))}
        <button className="btn-sm" onClick={() => handleInput(SAMPLE_SVG)}>Load Sample</button>
        <button className="btn-sm" onClick={() => { setInput(""); setOutput(""); setFiles([]); }}>Clear</button>
      </div>

      {mode === "upload" && (
        <DropZone accept=".svg,image/svg+xml" multiple={false} label="SVG file"
          files={files} onFiles={handleFiles} dragOver={dragOver} setDragOver={setDragOver} t={t} />
      )}

      <div className="grid-2">
        <div className="panel" style={{ border: `1px solid ${input.trim() ? t.borderActive : t.border}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 12px", background: t.bgPanelHead, borderBottom: `1px solid ${t.border}` }}>
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: t.textAccent }}>Input SVG</span>
            <span style={{ fontSize: 11, color: t.textMuted, fontFamily: "JetBrains Mono" }}>{formatBytes(originalSize)}</span>
          </div>
          <textarea value={input} onChange={e => handleInput(e.target.value)} placeholder="Paste SVG code here…" spellCheck={false}
            style={{ width: "100%", height: 240, background: "transparent", border: "none", outline: "none", padding: "12px 14px", color: t.textPrimary, fontFamily: "JetBrains Mono, monospace", fontSize: 11, lineHeight: 1.7, resize: "none" }} />
        </div>

        <div className="panel active-panel">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 12px", background: t.bgPanelHead, borderBottom: `1px solid ${t.border}` }}>
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: t.textAccent }}>Optimized SVG</span>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: 11, color: output && compressedSize < originalSize ? t.statusOk : t.textMuted, fontFamily: "JetBrains Mono" }}>{formatBytes(compressedSize)}</span>
              {output && <CopyBtn text={output} t={t} />}
              {output && <button className="btn-sm" onClick={() => downloadBlob(new Blob([output], { type: "image/svg+xml" }), files[0]?.name?.replace(".svg", "_opt.svg") || "optimized.svg")}>↓ Download</button>}
            </div>
          </div>
          <textarea readOnly value={output} placeholder="Optimized SVG appears here…" spellCheck={false}
            style={{ width: "100%", height: 240, background: "transparent", border: "none", outline: "none", padding: "12px 14px", color: t.textSecondary, fontFamily: "JetBrains Mono, monospace", fontSize: 11, lineHeight: 1.7, resize: "none" }} />
        </div>
      </div>

      {input && output && <StatsBar original={originalSize} compressed={compressedSize} t={t} />}

      <div style={{ background: t.bgChipActive, border: `1px solid ${t.border}`, borderRadius: 10, padding: "9px 14px", fontSize: 12, color: t.textSecondary, display: "flex", gap: 8 }}>
        <span>🔒</span> SVG optimization is <strong style={{ color: t.statusOk }}>lossless</strong> — removes comments, metadata, editor namespaces, redundant defaults, and excessive decimal precision. Visual output is identical.
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// 5. CODE COMPRESSOR  (JS / CSS / HTML / JSON — lossless minification)
// ══════════════════════════════════════════════════════════════════════════════
function CodeCompressor({ t }) {
  const [input, setInput]   = useState("");
  const [output, setOutput] = useState("");
  const [lang, setLang]     = useState("json");

  const SAMPLES = {
    json: '{\n  "name": "miniDevToolkit",\n  "version": "2.0.0",\n  "description": "Free developer tools",\n  "tools": ["json", "encoding", "generators"]\n}',
    css:  'body {\n  font-family: sans-serif;\n  margin: 0;\n  padding: 0;\n}\n\n.container {\n  max-width: 1200px;\n  margin: 0 auto;\n  padding: 0 1.5rem;\n}',
    html: '<!DOCTYPE html>\n<html lang="en">\n  <head>\n    <meta charset="UTF-8" />\n    <title>  My Page  </title>\n  </head>\n  <body>\n    <h1>  Hello World  </h1>\n    <p>  This is a paragraph.  </p>\n  </body>\n</html>',
  };

  const minify = useCallback((code, language) => {
    if (!code.trim()) return "";
    let s = code;
    if (language === "json") {
      try { return JSON.stringify(JSON.parse(s)); }
      catch { return "// Invalid JSON"; }
    }
    if (language === "css") {
      s = s.replace(/\/\*[\s\S]*?\*\//g, "");    // remove comments
      s = s.replace(/\s*{\s*/g, "{");
      s = s.replace(/\s*}\s*/g, "}");
      s = s.replace(/\s*:\s*/g, ":");
      s = s.replace(/\s*;\s*/g, ";");
      s = s.replace(/\s*,\s*/g, ",");
      s = s.replace(/\s+/g, " ");
      s = s.replace(/;\s*}/g, "}");
      return s.trim();
    }
    if (language === "html") {
      s = s.replace(/<!--[\s\S]*?-->/g, "");      // remove comments
      s = s.replace(/\s{2,}/g, " ");               // collapse whitespace
      s = s.replace(/>\s+</g, "><");               // remove space between tags
      s = s.replace(/\s+>/g, ">");
      s = s.replace(/<\s+/g, "<");
      return s.trim();
    }
    return s;
  }, []);

  const handleInput = (val) => {
    setInput(val);
    setOutput(minify(val, lang));
  };

  const handleLang = (l) => {
    setLang(l);
    setOutput(minify(input, l));
  };

  const originalSize   = new Blob([input]).size;
  const compressedSize = new Blob([output]).size;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        {[{id:"json",label:"JSON"},{id:"css",label:"CSS"},{id:"html",label:"HTML"}].map(l => (
          <button key={l.id} className="btn-sm" onClick={() => handleLang(l.id)}
            style={{ borderColor: lang===l.id ? t.borderActive : t.border, color: lang===l.id ? t.textAccent : t.textMuted, background: lang===l.id ? t.bgChipActive : "none", padding: "5px 16px" }}>
            {l.label}
          </button>
        ))}
        <button className="btn-sm" onClick={() => handleInput(SAMPLES[lang])}>Load Sample</button>
        <button className="btn-sm" onClick={() => { setInput(""); setOutput(""); }}>Clear</button>
      </div>

      <div className="grid-2">
        <div className="panel" style={{ border: `1px solid ${input.trim() ? t.borderActive : t.border}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 12px", background: t.bgPanelHead, borderBottom: `1px solid ${t.border}` }}>
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: t.textAccent }}>Input ({lang.toUpperCase()})</span>
            <span style={{ fontSize: 11, color: t.textMuted, fontFamily: "JetBrains Mono" }}>{formatBytes(originalSize)}</span>
          </div>
          <textarea value={input} onChange={e => handleInput(e.target.value)} placeholder={`Paste ${lang.toUpperCase()} to minify…`} spellCheck={false}
            style={{ width: "100%", height: 240, background: "transparent", border: "none", outline: "none", padding: "12px 14px", color: t.textPrimary, fontFamily: "JetBrains Mono, monospace", fontSize: 12, lineHeight: 1.7, resize: "none" }} />
        </div>

        <div className="panel active-panel">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 12px", background: t.bgPanelHead, borderBottom: `1px solid ${t.border}` }}>
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: t.textAccent }}>Minified Output</span>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: 11, color: output && compressedSize < originalSize ? t.statusOk : t.textMuted, fontFamily: "JetBrains Mono" }}>{formatBytes(compressedSize)}</span>
              {output && <CopyBtn text={output} t={t} />}
              {output && <button className="btn-sm" onClick={() => downloadBlob(new Blob([output]), `minified.${lang}`)}>↓ Download</button>}
            </div>
          </div>
          <textarea readOnly value={output} placeholder="Minified output appears here…" spellCheck={false}
            style={{ width: "100%", height: 240, background: "transparent", border: "none", outline: "none", padding: "12px 14px", color: t.textSecondary, fontFamily: "JetBrains Mono, monospace", fontSize: 12, lineHeight: 1.7, resize: "none" }} />
        </div>
      </div>

      {input && output && <StatsBar original={originalSize} compressed={compressedSize} t={t} />}

      <div style={{ background: t.bgChipActive, border: `1px solid ${t.border}`, borderRadius: 10, padding: "9px 14px", fontSize: 12, color: t.textSecondary, display: "flex", gap: 8 }}>
        <span>🔒</span> Code minification is <strong style={{ color: t.statusOk }}>lossless</strong> — removes comments and whitespace only. Functionality is 100% preserved.
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const TOOLS = [
  { id:"image", label:"Image",    icon:"🖼",  desc:"JPG / PNG / WebP — quality + resize" },
  { id:"pdf",   label:"PDF",      icon:"📄",  desc:"Re-render pages at lower quality" },
  { id:"zip",   label:"ZIP",      icon:"🗜",  desc:"Any files — lossless archive" },
  { id:"svg",   label:"SVG",      icon:"✦",   desc:"Remove metadata, comments, redundancy" },
  { id:"code",  label:"CSS/HTML/JSON", icon:"{ }", desc:"Minify code — lossless" },
];

export default function CompressionToolkit({ t }) {
  const [active, setActive] = useState("image");

  const components = {
    image: <ImageCompressor t={t} />,
    pdf:   <PdfCompressor   t={t} />,
    zip:   <ZipCompressor   t={t} />,
    svg:   <SvgOptimizer    t={t} />,
    code:  <CodeCompressor  t={t} />,
  };

  return (
    <div>
      {/* Hero */}
      <div className="tool-hero fade-up">
        <div className="tool-hero-inner">
          <div className="tool-hero-left">
            <h2>🗜 Compression Toolkit</h2>
            <p>Compress images, PDFs, ZIP archives, SVGs and code files — all in your browser. No uploads to any server.</p>
          </div>
          <div className="tool-hero-stats">
            <div className="tool-stat"><div className="tool-stat-val">5</div><div className="tool-stat-lbl">tools</div></div>
            <div className="tool-stat"><div className="tool-stat-val">3</div><div className="tool-stat-lbl">lossless</div></div>
            <div className="tool-stat"><div className="tool-stat-val">0</div><div className="tool-stat-lbl">server calls</div></div>
          </div>
        </div>
      </div>

      {/* Sub tabs */}
      <div className="pill-tab-bar" style={{ display:"flex", gap:6, padding:"12px 16px", flexWrap:"wrap" }}>
        {TOOLS.map(tool => {
          const isActive = active === tool.id;
          return (
            <button key={tool.id} onClick={() => setActive(tool.id)}
              className={`pill-tab ${isActive ? "active" : ""}`}>
              <span style={{ fontFamily:"JetBrains Mono", fontSize:11 }}>{tool.icon}</span>
              {tool.label}
            </button>
          );
        })}
      </div>

      <div className="tool-main">
        {components[active]}
      </div>

      <article className="tool-seo-section" aria-label="About Compression Toolkit">
        <h2>About Compression Toolkit</h2>
        <div className="tool-seo-cols">
          <div>
            <h3>How it works</h3>
            <ul>
              <li>Image compression uses the <code>browser-image-compression</code> library to reduce JPEG/PNG/WebP file sizes with a quality slider.</li>
              <li>ZIP creation uses the <code>fflate</code> library entirely in the browser.</li>
              <li>SVG, CSS, HTML, and JSON minification applies pattern-based whitespace and comment removal.</li>
              <li>No file is ever sent to a server — all compression is local.</li>
            </ul>
          </div>
          <div>
            <h3>Use cases</h3>
            <ul>
              <li>Optimize images for web pages to improve load times.</li>
              <li>Bundle multiple files into a ZIP for download or sharing.</li>
              <li>Minify CSS and HTML for production deployments.</li>
              <li>Reduce JSON payload sizes for API responses.</li>
            </ul>
          </div>
          <div>
            <h3>Privacy</h3>
            <ul>
              <li>All compression runs 100% in your browser.</li>
              <li>Images, code, and documents never leave your device.</li>
              <li>Free to use with no sign-up or account.</li>
            </ul>
          </div>
        </div>
        <h2 style={{ marginTop:"1.5rem" }}>Frequently Asked Questions</h2>
        <details><summary>How much can I compress a JPEG image?</summary><p>JPEG compression is lossy — lower quality settings produce smaller files but reduce visual fidelity. Typical web optimization (quality 70–80%) reduces file size by 50–80% with minimal visible quality loss. The tool shows original size, compressed size, and percentage saved.</p></details>
        <details><summary>What is the maximum file size for image compression?</summary><p>The Max File Size slider overrides the quality slider to guarantee the output does not exceed your specified size. This is useful when you have a strict size requirement (e.g., a form upload limit). The compression library will automatically find the quality level needed to meet the target size.</p></details>
        <details><summary>Can I add multiple files to the ZIP creator?</summary><p>Yes. The ZIP tool accepts multiple files by clicking "Add Files" multiple times or dragging and dropping a batch. Each file is added to the ZIP with its original filename. Click "Download ZIP" to generate and download the archive.</p></details>
        <details><summary>Does compression upload my files to a server?</summary><p>No. All compression runs entirely in your browser using browser-image-compression and fflate libraries. For SVG/CSS/HTML/JSON minification, pure JavaScript runs locally. Your files never leave your device.</p></details>
        <h2 style={{ marginTop:"1.5rem" }}>Alternatives &amp; why miniDevToolkit</h2>
        <p>Compared to <strong>Squoosh</strong> — Squoosh has superior image quality/size ratios with AVIF support; miniDevToolkit adds ZIP creation, PDF compression, SVG optimization, and CSS/HTML/JSON minification alongside image compression. Compared to <strong>ILovePDF</strong> — ILovePDF uploads files to servers; miniDevToolkit compresses PDFs entirely in your browser. Compared to <strong>CSS Minifier sites</strong> — miniDevToolkit bundles CSS, HTML, JSON minification alongside image and ZIP tools in one privacy-first toolkit. All compression is strictly client-side, free, and requires no sign-up.</p>
      </article>
    </div>
  );
}